import { SelectState as AriaKitSelectState } from "ariakit";
import React from "react";
import { ListChildComponentProps, VariableSizeList } from "react-window";
import styled from "styled-components";
import { PropOptionRenderItem } from "../PropX";
import { style } from "../style";
import { SelectItem } from "./SelectItem";

type Props = {
  state: AriaKitSelectState;
  items: PropOptionRenderItem[];
  overscanCount: number;
  mono?: boolean;
  compact?: boolean;
  "data-testid"(value: string): string;
  UNSAFE_precalculatedItemHeights?: Map<string, number>;
};

const GROUP_TITLE_PADDING_TOP = 10;
const GROUP_TITLE_PADDING_BOTTOM = 4;
const GROUP_TITLE_HEIGHT =
  GROUP_TITLE_PADDING_TOP + GROUP_TITLE_PADDING_BOTTOM + parseInt(style.vars.fontSize.small, 10) * parseFloat(style.vars.lineHeight);
const GROUP_SPACER_HEIGHT = 6;

const GroupSpacer = styled.div`
  all: unset;
  display: block;
  height: ${GROUP_SPACER_HEIGHT}px;
`;

const GroupTitle = styled.div`
  all: unset;

  display: block;

  padding-left: 16px;
  padding-top: ${GROUP_TITLE_PADDING_TOP}px;
  padding-bottom: ${GROUP_TITLE_PADDING_BOTTOM}px;
  color: ${style.colors.grey.dark};
  font-weight: ${style.vars.fontWeight.bold};
  font-size: ${style.vars.fontSize.small};
  text-transform: uppercase;
`;

const renderItem: React.FC<ListChildComponentProps<Props>> = (props) => {
  const { data, index, style } = props;
  const { mono = false, compact = false, state } = data;

  const item = data.items[index];

  switch (item.type) {
    case "group-start":
      return (
        <div style={style}>
          <GroupTitle>{item.title}</GroupTitle>
        </div>
      );
    case "group-end":
      return (
        <div style={style}>
          <GroupSpacer />
        </div>
      );
    case "option": {
      const testId = data["data-testid"]?.(item.option.value);

      return (
        <div style={style}>
          <SelectItem option={item.option} state={state} compact={compact} mono={mono} data-testid={testId} />
        </div>
      );
    }
  }
};

export const SelectList: React.FC<Props> = (props) => {
  const { items, compact = false, UNSAFE_precalculatedItemHeights = new Map() } = props;

  const listRef = React.useRef<VariableSizeList>(null);

  let overscanCount = props.overscanCount;
  /**
   * HACK! HACK! HACK!
   *
   * Because we are using `react-window` to render the list, we need to make sure
   * that we set the `overscanCount` to a very large number when running jest.
   * The `overscanCount` tells `react-window` how many items to render above and
   * below the visible items in the list. By default, we set this to `10` for performance,
   * but in a unit testing environment we don't want to worry about this kind of detail.
   * Just render all of the items.
   */
  if (typeof jest !== "undefined") {
    overscanCount = 100_000;
  }

  /**
   * react-window is very aggressive with caching the height of items, so when
   * the groups change, we need to reset the cache. By calling `resetAfterIndex`
   * with `0`, we are telling react-window to reset the cache for all items.
   *
   * Potential performance improvement:
   *   Keep track of the previous groups and only reset the cache after
   *   finding a group that has changed its length.
   */
  React.useEffect(() => listRef.current?.resetAfterIndex(0), [items]);

  const calculateItemSize = (index: number): number => {
    const item = items[index];

    switch (item.type) {
      case "group-start":
        if (item.title === "") {
          return 0;
        }
        return GROUP_TITLE_HEIGHT;
      case "group-end":
        return GROUP_SPACER_HEIGHT;
      case "option": {
        const minHeight = SelectItem.__INTERNAL__fastCalculateOuterHeight({ compact });
        const height = UNSAFE_precalculatedItemHeights.get(item.option.value) ?? 0;

        return Math.max(minHeight, height);
      }
    }
  };

  const calculateItemKey = (index: number): string => {
    const item = items[index];

    switch (item.type) {
      case "group-start":
        return `title-${item.title}`;
      case "group-end":
        return `spacer-${index}`;
      case "option":
        return `option-${item.option.value}`;
    }
  };

  /**
   * We want the list to have a maximum height of 250, so iterate through the
   * groups and if we calculate a number greater than 250, just return 250.
   * Otherwise, return the calculated height. This can really only happen when there
   * is only one item in the list.
   */
  const height = React.useMemo(() => {
    let calculatedHeight = 0;

    for (let i = 0; i < items.length; i++) {
      calculatedHeight += calculateItemSize(i);

      if (calculatedHeight >= 250) {
        return 250;
      }
    }

    return calculatedHeight;
  }, [items]);

  return (
    <VariableSizeList
      ref={listRef}
      itemSize={calculateItemSize}
      itemKey={calculateItemKey}
      itemData={props}
      itemCount={items.length}
      width="auto"
      height={height}
      overscanCount={overscanCount}
    >
      {renderItem}
    </VariableSizeList>
  );
};

SelectList.displayName = "SelectList";
